// Inventory model. SLOT-BASED: a fixed number of slots, each holding one stack {id, n} up to that
// item's per-slot stack limit. A full stack overflows into a NEW stack in a free slot; when no slot
// can take the item, add() returns what actually fit (callers warn / re-drop the rest). The HUD and
// panels subscribe via subscribe(). PlayerInventory (below) adds the 10-slot hotbar as real storage.

// Consumables (eaten/used from a hotbar slot). Defined as their own union so the consumable table + the
// hotbar can both reference it without an import cycle (inventory imports nothing from those modules).
export type FoodId =
  | 'mushroom' | 'mushroom_cooked'
  | 'cactus_flesh' | 'cactus_cooked'
  | 'pumpkin' | 'pumpkin_cooked'
  | 'corn' | 'corn_cooked'
  | 'berries'
  | 'meat_deer_raw' | 'meat_deer_cooked'
  | 'meat_chicken_raw' | 'meat_chicken_cooked'
  | 'meat_boar_raw' | 'meat_boar_cooked'
  | 'meat_cow_raw' | 'meat_cow_cooked'
  | 'coconut' | 'apple'
  | 'bandage';

// Wieldable tools (held in hand). rock + torch are the starter pair; the rest are crafted. Axes chop
// trees faster, pickaxes mine ore/stone faster, by tier (wood < stone < metal). See TOOLS in config.
export type ToolId =
  | 'rock' | 'torch' | 'hammer' | 'blueprint' | 'guidebook'
  | 'wood_axe' | 'wood_pickaxe' | 'stone_axe' | 'stone_pickaxe' | 'metal_axe' | 'metal_pickaxe';

// Crop seeds (game/farming.ts): planted in a PLANTER deployable, grown on the game-day clock, harvested
// for food + a seed back. Own union so farming can type its maps without importing the whole item table.
export type SeedId = 'seed_corn' | 'seed_pumpkin' | 'seed_berry';

// Loot / recycler components: barrel + crate + junk-pile drops and the recycler's output resources. Name-only
// for now (no icons / stack tuning); loot tables + recycling wire these up in later phases.
export type LootId =
  | 'salvage' | 'cogs' | 'blade' | 'pipe' | 'spring' | 'rope' | 'tarp' | 'panels' | 'stitching_pack'
  | 'metal_plating' | 'electronics' | 'gun_receiver' | 'gun_frame' | 'long_gun_frame' | 'gas_tank'
  | 'fuse' | 'camera_lens' | 'computer_parts' | 'light_fuel' | 'refined_fuel' | 'armor' | 'cloth';

import { CRAFTABLES, CRAFTABLE_IDS, type CraftableId } from './craftables';
import { WEAPONS, AMMO_META, WEAPON_IDS, AMMO_IDS, type WeaponId, type AmmoId } from './weapons';

export type ItemId =
  | CraftableId // craftable deployables (benches, furnaces, doors, traps, ...) -- see craftables.ts
  | WeaponId | AmmoId // combat: wieldable weapons + their ammo/throwables -- see weapons.ts
  | 'wood' | 'stone' | 'metal' | 'metal_ore' | 'refined_metal' | 'sulfur' | 'rock' | 'torch' | 'bone' | 'fat' | 'charcoal'
  | 'hammer' | 'blueprint' | 'guidebook' // craftable tools (build + the gather rock/torch) + the field guide
  | 'wood_axe' | 'wood_pickaxe' | 'stone_axe' | 'stone_pickaxe' | 'metal_axe' | 'metal_pickaxe' // tiered gather tools
  | 'chest' | 'bigchest' // craftable deployables (placed from a hotbar slot)
  | 'campfire' // craftable deployable: cooking station
  | FoodId
  | SeedId
  | LootId;

export const ITEMS: Record<ItemId, { name: string; color: number }> = {
  wood: { name: 'Wood', color: 0x9b6b3a },
  stone: { name: 'Stone', color: 0x8a8f96 },
  metal_ore: { name: 'Metal Ore', color: 0x8a6f52 }, // mined raw; the furnace smelts this into Metal
  metal: { name: 'Metal', color: 0xb0884f },
  refined_metal: { name: 'Refined Metal', color: 0xc3c9d4 }, // refinery re-smelts metal 10:1; the reinforced build tier's cost
  sulfur: { name: 'Sulfur', color: 0xd9c34a },
  rock: { name: 'Rock', color: 0x6f7479 },
  torch: { name: 'Torch', color: 0xffa64d },
  guidebook: { name: 'Guide', color: 0x9a6b3f }, // the field guide: using it (left-click) opens the OXIDE book overlay

  bone: { name: 'Bone', color: 0xe8e2cf }, // harvested by chopping a corpse
  fat: { name: 'Body Fat', color: 0xe9c98a }, // harvested by chopping a corpse
  charcoal: { name: 'Charcoal', color: 0x33302b }, // campfire/furnace byproduct: each log burned leaves one
  // Loot + recycler components (LootId) -- name-only placeholders for now.
  salvage: { name: 'Salvage', color: 0x9aa0a6 },
  cogs: { name: 'Cogs', color: 0x8c8f95 },
  blade: { name: 'Blade', color: 0xc2c7cd },
  pipe: { name: 'Pipe', color: 0x7c8086 },
  spring: { name: 'Spring', color: 0xb4b9c0 },
  rope: { name: 'Rope', color: 0xb89b6a },
  tarp: { name: 'Tarp', color: 0x5f7d6a },
  panels: { name: 'Panels', color: 0xa8a060 },
  stitching_pack: { name: 'Stitching Pack', color: 0xd8c9a8 },
  metal_plating: { name: 'Metal Plating', color: 0x9498a0 },
  electronics: { name: 'Electronics', color: 0x3f8f5a },
  gun_receiver: { name: 'Gun Receiver', color: 0x6b6f76 },
  gun_frame: { name: 'Gun Frame', color: 0x60656c },
  long_gun_frame: { name: 'Long Gun Frame', color: 0x565b62 },
  gas_tank: { name: 'Gas Tank', color: 0xc24a3a },
  fuse: { name: 'Fuse', color: 0xd9a23a },
  camera_lens: { name: 'Camera Lens', color: 0x4a6f9a },
  computer_parts: { name: 'Computer Parts', color: 0x3f7f8f },
  light_fuel: { name: 'Light Fuel', color: 0xd0b048 },
  refined_fuel: { name: 'Refined Fuel', color: 0xc26a2a },
  armor: { name: 'Armor', color: 0x8a8d93 },
  cloth: { name: 'Cloth', color: 0xc9b79a },
  hammer: { name: 'Hammer', color: 0x9aa0a6 },
  blueprint: { name: 'Blueprint', color: 0x2f6fb3 },
  wood_axe: { name: 'Wooden Axe', color: 0x8a6a3a },
  wood_pickaxe: { name: 'Wooden Pickaxe', color: 0x7a5e34 },
  stone_axe: { name: 'Stone Axe', color: 0x8f9298 },
  stone_pickaxe: { name: 'Stone Pickaxe', color: 0x82868c },
  metal_axe: { name: 'Metal Axe', color: 0xc2c7cd },
  metal_pickaxe: { name: 'Metal Pickaxe', color: 0xb4b9c0 },
  chest: { name: 'Small Chest', color: 0x8a5f37 },
  bigchest: { name: 'Large Chest', color: 0x6e4a2a },
  campfire: { name: 'Campfire', color: 0xd06a2a },
  mushroom: { name: 'Mushroom', color: 0xc89a6a },
  mushroom_cooked: { name: 'Cooked Mushroom', color: 0xa9743f },
  cactus_flesh: { name: 'Cactus Flesh', color: 0x6fae5a },
  cactus_cooked: { name: 'Cooked Cactus', color: 0x8a9a4a },
  pumpkin: { name: 'Pumpkin', color: 0xe0822c },
  pumpkin_cooked: { name: 'Cooked Pumpkin', color: 0xc06820 },
  corn: { name: 'Corn', color: 0xe6c83a },
  corn_cooked: { name: 'Cooked Corn', color: 0xd9b028 },
  berries: { name: 'Berries', color: 0x8a4fb0 },
  coconut: { name: 'Coconut', color: 0x7a4a2e }, // palm-drop food: mostly thirst, a little hunger
  apple: { name: 'Apple', color: 0xc4392f },     // rare tree fruit: a crisp hunger snack
  seed_corn: { name: 'Corn Seeds', color: 0xd9c47a },
  seed_pumpkin: { name: 'Pumpkin Seeds', color: 0xc98a4a },
  seed_berry: { name: 'Berry Seeds', color: 0x9a6ab8 },
  meat_deer_raw: { name: 'Raw Venison', color: 0xb05646 },
  meat_deer_cooked: { name: 'Cooked Venison', color: 0x824631 },
  meat_chicken_raw: { name: 'Raw Chicken', color: 0xd99a86 },
  meat_chicken_cooked: { name: 'Cooked Chicken', color: 0xb07a4a },
  meat_boar_raw: { name: 'Raw Pork', color: 0xc46a5e },
  meat_boar_cooked: { name: 'Cooked Pork', color: 0x8f5238 },
  meat_cow_raw: { name: 'Raw Beef', color: 0xa84a44 },
  meat_cow_cooked: { name: 'Cooked Beef', color: 0x7a3e2e },
  bandage: { name: 'Bandage', color: 0xeae3d5 },
  // Craftable deployables (CraftableId) -- names from CRAFTABLES, one neutral swatch colour for the menu.
  ...(Object.fromEntries(CRAFTABLES.map((c) => [c.id, { name: c.label, color: 0x8a8f96 }])) as Record<CraftableId, { name: string; color: number }>),
  // Weapons + ammo (WeaponId / AmmoId) -- names + colours from weapons.ts.
  ...(Object.fromEntries(WEAPON_IDS.map((id) => [id, { name: WEAPONS[id].name, color: WEAPONS[id].color }])) as Record<WeaponId, { name: string; color: number }>),
  ...(Object.fromEntries(AMMO_IDS.map((id) => [id, AMMO_META[id]])) as Record<AmmoId, { name: string; color: number }>),
};

// The four primary gatherable resources, shown in the HUD bar.
export const PRIMARY_RESOURCES: ItemId[] = ['wood', 'stone', 'metal_ore', 'sulfur'];

// Runtime list of the FoodId union (consumables.ts owns the effects table; this list must stay in sync
// with the FoodId type above -- inventory can't import consumables without a cycle).
export const FOOD_IDS: readonly FoodId[] = [
  'mushroom', 'mushroom_cooked', 'cactus_flesh', 'cactus_cooked', 'pumpkin', 'pumpkin_cooked',
  'corn', 'corn_cooked', 'berries', 'meat_deer_raw', 'meat_deer_cooked', 'meat_chicken_raw', 'meat_chicken_cooked',
  'meat_boar_raw', 'meat_boar_cooked', 'meat_cow_raw', 'meat_cow_cooked', 'coconut', 'apple', 'bandage',
];
const TOOL_IDS: readonly ItemId[] = ['rock', 'torch', 'hammer', 'blueprint', 'guidebook',
  'wood_axe', 'wood_pickaxe', 'stone_axe', 'stone_pickaxe', 'metal_axe', 'metal_pickaxe'];

// PER-SLOT STACK LIMITS. A slot holds at most this many of the item; more spills into a new stack in the
// next free slot. Bulk resources stack 1000; tools + weapons stack 1 (they carry durability); food stacks
// mid; HEAVY components stay tight (think weight). NOTE: a Base Claim costs 1000 wood = exactly one stack.
export const STACK_LIMIT: Partial<Record<ItemId, number>> = {
  wood: 1000, stone: 1000, metal: 1000, metal_ore: 1000, refined_metal: 1000, sulfur: 1000,
  cloth: 1000, bone: 1000, fat: 1000, charcoal: 1000,
  salvage: 1000, light_fuel: 500, refined_fuel: 500, rope: 50, tarp: 20, panels: 20, armor: 20,
  cogs: 20, metal_plating: 20, electronics: 50, pipe: 20, spring: 20, blade: 20, stitching_pack: 20,
  gun_receiver: 20, gun_frame: 20, long_gun_frame: 20, gas_tank: 10, fuse: 10, camera_lens: 10, computer_parts: 10,
  // Weapons + tools wield one at a time (stack 1, they wear); throwables carry a few; ammo stacks deep.
  ...Object.fromEntries(WEAPON_IDS.map((id) => [id, 1])),
  ...Object.fromEntries(TOOL_IDS.map((id) => [id, 1])),
  grenade: 10, c4: 5, satchel_charge: 10,
  arrows: 64, ammo_9mm: 128, ammo_554: 128, ammo_554_explosive: 64, shotgun_shell: 64, shotgun_shell_incendiary: 64, rocket: 10,
  // Food is perishable-bulky: a slot holds a meal run, not a warehouse.
  ...Object.fromEntries(FOOD_IDS.map((id) => [id, 20])),
  bandage: 10,
  seed_corn: 50, seed_pumpkin: 50, seed_berry: 50, // seeds are pocketable (a planter takes 1 at a time)
  // Placeable deployables (benches/furnaces/doors/chests/...) stack a handful per slot.
  chest: 10, bigchest: 10, campfire: 10,
  ...Object.fromEntries([...CRAFTABLE_IDS].map((id) => [id, 10])),
};
export const STACK_DEFAULT = 100; // anything unlisted
export function stackLimit(id: ItemId): number { return STACK_LIMIT[id] ?? STACK_DEFAULT; }

// The only items a Base Claim will hold (it is a restricted medium chest). refined_metal is in so a
// reinforced-tier base can actually pay its upkeep (upkeep bills in each tier's build item).
export const BASECLAIM_ACCEPT: ReadonlySet<ItemId> = new Set<ItemId>(['wood', 'stone', 'metal', 'refined_metal', 'armor']);

// The only items a Fridge will hold (food, raw or cooked -- it preserves food; bandages excluded).
export const FOOD_ACCEPT: ReadonlySet<ItemId> = new Set<ItemId>([
  'mushroom', 'mushroom_cooked', 'cactus_flesh', 'cactus_cooked', 'pumpkin', 'pumpkin_cooked',
  'corn', 'corn_cooked', 'berries', 'meat_deer_raw', 'meat_deer_cooked', 'meat_chicken_raw', 'meat_chicken_cooked',
  'meat_boar_raw', 'meat_boar_cooked', 'meat_cow_raw', 'meat_cow_cooked',
]);

// Items that are PLACED in the world from a hotbar slot (consumed one at a time on deploy) rather than
// stacked as raw resources. Drag one onto a hotbar slot to wield it.
export const DEPLOYABLES: ReadonlySet<ItemId> = new Set<ItemId>(['chest', 'bigchest', 'campfire', ...CRAFTABLE_IDS]);

// HOTBAR ELIGIBILITY. The bar only holds things you can DO something with in hand -- raw resources,
// components and ammo can never sit there (dead obvious: wood is not wieldable).
//   HOTBAR_AUTO:    goes straight to the bar when crafted/picked up (tools, weapons, food).
//   HOTBAR_ALLOWED: may be dragged onto the bar (AUTO + placeables, which craft into the inventory first).
export const HOTBAR_AUTO: ReadonlySet<ItemId> = new Set<ItemId>([...TOOL_IDS, ...WEAPON_IDS, ...FOOD_IDS]);
export const HOTBAR_ALLOWED: ReadonlySet<ItemId> = new Set<ItemId>([...HOTBAR_AUTO, ...DEPLOYABLES]);

// One stack in one slot.
export interface Stack { id: ItemId; n: number; }

export class Inventory {
  protected readonly slots: Array<Stack | null>;
  private readonly listeners: Array<() => void> = [];

  constructor(readonly size = 36) {
    this.slots = new Array(size).fill(null);
  }

  // Subscribe to changes (the HUD, panels and the hotbar all listen). Returns an unsubscribe fn.
  subscribe(fn: () => void): () => void {
    this.listeners.push(fn);
    return () => {
      const i = this.listeners.indexOf(fn);
      if (i >= 0) this.listeners.splice(i, 1);
    };
  }

  protected emit(): void {
    for (const fn of this.listeners) fn();
  }

  // The stack in a slot (UI paints the grid from this). null = empty slot.
  stackAt(i: number): Stack | null { return this.slots[i] ?? null; }

  // QoL SORT: merge every stack of one item together, then pack alphabetically to the top-left,
  // re-split by the item's stack limit. One click tidies a looted-out grid (owner spec).
  sortMerge(): void {
    const totals = new Map<ItemId, number>();
    for (const s of this.slots) if (s) totals.set(s.id, (totals.get(s.id) ?? 0) + s.n);
    this.slots.fill(null);
    let i = 0;
    for (const [id, n] of [...totals.entries()].sort((a, b) => a[0].localeCompare(b[0]))) {
      const limit = stackLimit(id);
      let left = n;
      while (left > 0 && i < this.slots.length) { const put = Math.min(limit, left); this.slots[i++] = { id, n: put }; left -= put; }
    }
    this.emit();
  }

  // Fill partial stacks of `id` first, then open new stacks in free slots. No emit -- public ops emit once.
  protected fillSlots(slots: Array<Stack | null>, id: ItemId, n: number): number {
    const limit = stackLimit(id);
    let left = n;
    for (const s of slots) { // top up existing stacks
      if (left <= 0) break;
      if (s && s.id === id && s.n < limit) { const put = Math.min(left, limit - s.n); s.n += put; left -= put; }
    }
    for (let i = 0; i < slots.length && left > 0; i++) { // then start new stacks
      if (!slots[i]) { const put = Math.min(left, limit); slots[i] = { id, n: put }; left -= put; }
    }
    return n - left;
  }

  // Drain stacks of `id` from the END backwards (partials/newest first). No emit.
  protected drainSlots(slots: Array<Stack | null>, id: ItemId, n: number): number {
    let left = n;
    for (let i = slots.length - 1; i >= 0 && left > 0; i--) {
      const s = slots[i];
      if (!s || s.id !== id) continue;
      const got = Math.min(left, s.n);
      s.n -= got; left -= got;
      if (s.n <= 0) slots[i] = null;
    }
    return n - left;
  }

  protected roomIn(slots: Array<Stack | null>, id: ItemId): number {
    const limit = stackLimit(id);
    let r = 0;
    for (const s of slots) r += s ? (s.id === id ? limit - s.n : 0) : limit;
    return r;
  }

  // Add up to what the slots can hold; returns how many ACTUALLY fit (callers warn / re-drop the overflow).
  add(id: ItemId, n = 1): number {
    if (n <= 0) return 0;
    const added = this.fillSlots(this.slots, id, n);
    if (added > 0) this.emit();
    return added;
  }

  // Free space left for this item across partial stacks + empty slots. 0 = no room at all.
  room(id: ItemId): number { return this.roomIn(this.slots, id); }

  // Add up to n of `id` into ONE specific slot (typed stations place fuel/input/output in fixed slots instead of
  // first-fit). Merges with a same-id stack or fills an empty slot up to the stack limit; a slot already holding a
  // DIFFERENT item takes nothing. Returns how many actually landed. Emits once when something changed.
  addToSlot(i: number, id: ItemId, n = 1): number {
    if (n <= 0 || i < 0 || i >= this.slots.length) return 0;
    const limit = stackLimit(id);
    const s = this.slots[i];
    if (!s) { const put = Math.min(n, limit); this.slots[i] = { id, n: put }; this.emit(); return put; }
    if (s.id !== id) return 0;
    const put = Math.min(n, limit - s.n);
    if (put > 0) { s.n += put; this.emit(); }
    return put;
  }

  take(id: ItemId, n: number): number {
    const taken = this.drainSlots(this.slots, id, n);
    if (taken > 0) this.emit();
    return taken;
  }

  // FREE PLACEMENT (owner: organize the grid at will): drop the stack at slot `from` onto slot `to` within
  // THIS inventory. Empty target -> move; same item -> merge up to the stack limit (overflow stays at `from`);
  // different item -> swap. Emits once. This is what lets you drag any stack to any slot, not just auto-stack.
  moveOrSwap(from: number, to: number): void {
    if (from === to || from < 0 || to < 0 || from >= this.slots.length || to >= this.slots.length) return;
    const a = this.slots[from];
    if (!a) return;
    const b = this.slots[to];
    if (!b) { this.slots[to] = a; this.slots[from] = null; }              // move into an empty slot
    else if (b.id === a.id) {                                             // merge two stacks of the same item
      const put = Math.min(a.n, stackLimit(a.id) - b.n);
      b.n += put; a.n -= put;
      if (a.n <= 0) this.slots[from] = null;                             // fully merged (else the overflow stays put)
    } else { this.slots[from] = b; this.slots[to] = a; }                  // swap two different items
    this.emit();
  }

  // Take up to n from ONE specific slot (drag/drop moves an exact stack). Returns what came out.
  takeFromSlot(i: number, n: number): number {
    const s = this.slots[i];
    if (!s || n <= 0) return 0;
    const got = Math.min(n, s.n);
    s.n -= got;
    if (s.n <= 0) this.slots[i] = null;
    if (got > 0) this.emit();
    return got;
  }

  count(id: ItemId): number {
    let c = 0;
    for (const s of this.slots) if (s && s.id === id) c += s.n;
    return c;
  }

  // Aggregated totals per item (feed lines, corpse loot, upkeep and admin all read these).
  entries(): Array<[ItemId, number]> {
    const m = new Map<ItemId, number>();
    for (const s of this.slots) if (s) m.set(s.id, (m.get(s.id) ?? 0) + s.n);
    return [...m.entries()];
  }

  isEmpty(): boolean {
    return this.slots.every((s) => !s);
  }

  // Move everything into another inventory (used when looting a container). Respects the dest's slot
  // capacity: whatever doesn't fit STAYS here (no item loss).
  moveAllTo(dest: Inventory): void {
    for (const [id, n] of this.entries()) { const moved = dest.add(id, n); if (moved > 0) this.take(id, moved); }
  }

  // --- Local world save (game/persist.ts): the EXACT slot layout in/out, so a reload puts every
  // stack back where the player left it. Unknown ids (a save from an older build whose item was
  // removed) load as an empty slot instead of corrupting the grid. ---
  serializeSlots(): Array<Stack | null> {
    return this.slots.map((s) => (s ? { id: s.id, n: s.n } : null));
  }
  deserializeSlots(saved: ReadonlyArray<Stack | null>): void {
    for (let i = 0; i < this.slots.length; i++) {
      const s = i < saved.length ? saved[i] : null;
      this.slots[i] = s && s.id in ITEMS && s.n > 0 ? { id: s.id, n: s.n } : null;
    }
    this.emit();
  }
}

// THE PLAYER'S carry: a 6x6 grid plus the 10-slot hotbar as REAL storage (an item lives in the grid OR
// in a bar slot, never both). add() routes by eligibility: tools/weapons/food land on the bar first
// (top-up, then free bar slots), placeables + resources go to the grid. take()/count() see both, so
// gameplay checks ("do I have a torch?") keep working wherever the stack sits.
export const BAR_SIZE = 6;
export const GRID_SIZE = 24; // backpack grid: 6 columns x 4 rows (the loadout/storage right pane)

export class PlayerInventory extends Inventory {
  private readonly bar: Array<Stack | null> = new Array(BAR_SIZE).fill(null);

  constructor() { super(GRID_SIZE); } // 24-slot backpack (the hotbar's 6 are the separate `bar` above)

  // --- bar access (the Hotbar + its UI read these; slot0 is 0-based) ---
  barStack(slot0: number): Stack | null { return this.bar[slot0] ?? null; }

  override count(id: ItemId): number {
    let c = super.count(id);
    for (const s of this.bar) if (s && s.id === id) c += s.n;
    return c;
  }

  override room(id: ItemId): number {
    return super.room(id) + (HOTBAR_AUTO.has(id) ? this.roomIn(this.bar, id) : 0);
  }

  override add(id: ItemId, n = 1): number {
    if (n <= 0) return 0;
    let left = n;
    if (HOTBAR_AUTO.has(id)) left -= this.fillSlots(this.bar, id, left); // bar first (crafted/picked up wieldables)
    left -= this.fillSlots(this.slots, id, left);
    const added = n - left;
    if (added > 0) this.emit();
    return added;
  }

  // Drain the BAR first (that's the stack you're actively using -- eating from a slot, placing a
  // deployable, a torch burning out), then the grid.
  override take(id: ItemId, n: number): number {
    let got = this.drainSlots(this.bar, id, n);
    got += this.drainSlots(this.slots, id, n - got);
    if (got > 0) this.emit();
    return got;
  }

  override entries(): Array<[ItemId, number]> {
    const m = new Map<ItemId, number>();
    for (const s of [...this.slots, ...this.bar]) if (s) m.set(s.id, (m.get(s.id) ?? 0) + s.n);
    return [...m.entries()];
  }

  override isEmpty(): boolean {
    return super.isEmpty() && this.bar.every((s) => !s);
  }

  // --- moves between the grid and the bar (drag/drop). All whole-stack. ---

  // Grid slot -> bar slot. Occupied bar slot swaps back into the source grid slot. Rejects ineligible items.
  moveGridToBar(gridIdx: number, barIdx: number): boolean {
    const s = this.stackAt(gridIdx);
    if (!s || barIdx < 0 || barIdx >= BAR_SIZE || !HOTBAR_ALLOWED.has(s.id)) return false;
    const prev = this.bar[barIdx];
    // Same item already there: merge up to the stack limit instead of swapping.
    if (prev && prev.id === s.id && prev.n < stackLimit(s.id)) {
      const put = Math.min(s.n, stackLimit(s.id) - prev.n);
      prev.n += put; s.n -= put;
      if (s.n <= 0) this.slots[gridIdx] = null;
    } else {
      this.bar[barIdx] = s;
      this.slots[gridIdx] = prev; // may be null (plain move) or the displaced stack (swap)
    }
    this.emit();
    return true;
  }

  // Bar slot -> grid (merge into partials, then free slots). Whatever doesn't fit stays on the bar.
  moveBarToGrid(barIdx: number): boolean {
    const s = this.bar[barIdx];
    if (!s) return false;
    const moved = this.fillSlots(this.slots, s.id, s.n);
    if (moved <= 0) return false;
    s.n -= moved;
    if (s.n <= 0) this.bar[barIdx] = null;
    this.emit();
    return true;
  }

  swapBarSlots(a: number, b: number): void {
    if (a < 0 || b < 0 || a >= BAR_SIZE || b >= BAR_SIZE || a === b) return;
    const tmp = this.bar[a]; this.bar[a] = this.bar[b]; this.bar[b] = tmp;
    this.emit();
  }

  // Take up to n out of ONE bar slot (moving a bar stack into open storage).
  takeFromBarSlot(barIdx: number, n: number): number {
    const s = this.bar[barIdx];
    if (!s || n <= 0) return 0;
    const got = Math.min(n, s.n);
    s.n -= got;
    if (s.n <= 0) this.bar[barIdx] = null;
    if (got > 0) this.emit();
    return got;
  }

  // Rip a whole stack off the bar (the 3rd-person fling-to-ground). Returns it, or null.
  takeBarStack(barIdx: number): Stack | null {
    const s = this.bar[barIdx];
    if (!s) return null;
    this.bar[barIdx] = null;
    this.emit();
    return s;
  }

  // Take a whole GRID stack out (drag a stack onto empty space = drop it on the ground).
  takeGridStack(gridIdx: number): Stack | null {
    const s = this.slots[gridIdx];
    if (!s) return null;
    this.slots[gridIdx] = null;
    this.emit();
    return s;
  }

  // --- Local world save: the hotbar is real storage, so it round-trips slot-exact too. ---
  serializeBar(): Array<Stack | null> {
    return this.bar.map((s) => (s ? { id: s.id, n: s.n } : null));
  }
  deserializeBar(saved: ReadonlyArray<Stack | null>): void {
    for (let i = 0; i < this.bar.length; i++) {
      const s = i < saved.length ? saved[i] : null;
      this.bar[i] = s && s.id in ITEMS && s.n > 0 ? { id: s.id, n: s.n } : null;
    }
    this.emit();
  }
}
